import { useState, useRef } from 'react'
import type { MachineProfile } from '../../types'
import { CONTROLLERS } from './MachineSelector'

// Default header/footer templates per controller type
const CONTROLLER_DEFAULTS: Record<string, { header: string; footer: string }> = {
  'Fanuc 0i': {
    header: `%\nO0001\n(DATE - )\n(MACHINE - )\n(MATERIAL - )\n\nG00 G17 G40 G49 G80\nG21`,
    footer: `G00 G91 G28 Z0.0\nG00 G91 G28 Y0\nM01\nM30\n%`,
  },
  'Fanuc 30i': {
    header: `%\nO0001\n(DATE - )\n(MACHINE - )\n(MATERIAL - )\n\nG00 G17 G40 G49 G80\nG21`,
    footer: `G00 G91 G28 Z0.0\nG00 G91 G28 Y0\nM01\nM30\n%`,
  },
  'Haas NGC': {
    header: `%\nO00001\n(DATE - )\n(MACHINE - )\n(MATERIAL - )\n\nG00 G17 G40 G49 G80\nG21`,
    footer: `G00 G91 G28 Z0.0\nG00 G91 G28 Y0\nM01\nM30\n%`,
  },
  'Haas Classic': {
    header: `%\nO00001\n(DATE - )\n(MACHINE - )\n(MATERIAL - )\n\nG00 G17 G40 G49 G80\nG21`,
    footer: `G00 G91 G28 Z0.0\nG00 G91 G28 Y0\nM01\nM30\n%`,
  },
  'Siemens 840D': {
    header: `%\nO0001\n(DATE - )\n(MACHINE - )\n(MATERIAL - )\n\nG00 G17 G40 G49 G80\nG21`,
    footer: `G00 G91 G28 Z0.0\nG00 G91 G28 Y0\nM01\nM30\n%`,
  },
  'Mazak': {
    header: `%\nO0001\n(DATE - )\n(MACHINE - )\n(MATERIAL - )\n\nG00 G17 G40 G49 G80\nG21`,
    footer: `G00 G91 G28 Z0.0\nG00 G91 G28 Y0\nM01\nM30\n%`,
  },
}

function getDefaultForController(controller: string) {
  return CONTROLLER_DEFAULTS[controller] ?? CONTROLLER_DEFAULTS['Fanuc 0i']
}

interface MachineProfileEditorProps {
  value: MachineProfile
  onChange: (profile: MachineProfile) => void
  onSave: () => void
  onCancel: () => void
  existingNames: string[]
}

export default function MachineProfileEditor({ value, onChange, onSave, onCancel, existingNames }: MachineProfileEditorProps) {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [templateOpen, setTemplateOpen] = useState(false)
  const prevControllerRef = useRef(value.controller)

  const update = (patch: Partial<MachineProfile>) => {
    // Auto-populate templates when controller changes (if empty or matching previous default)
    if (patch.controller && patch.controller !== value.controller) {
      const prevDefaults = getDefaultForController(value.controller)
      const newDefaults = getDefaultForController(patch.controller)
      const headerIsDefault = !value.headerTemplate?.trim() || value.headerTemplate.trim() === prevDefaults.header.trim()
      const footerIsDefault = !value.footerTemplate?.trim() || value.footerTemplate.trim() === prevDefaults.footer.trim()
      if (headerIsDefault) patch.headerTemplate = newDefaults.header
      if (footerIsDefault) patch.footerTemplate = newDefaults.footer
      prevControllerRef.current = patch.controller
    }
    onChange({ ...value, ...patch })
  }

  const handleSave = () => {
    const errs: Record<string, string> = {}
    if (!value.name.trim()) errs.name = 'Name is required'
    else if (existingNames.some(n => n.toLowerCase() === value.name.trim().toLowerCase())) errs.name = 'A machine with this name already exists'
    if (Object.keys(errs).length > 0) { setErrors(errs); return }
    onSave()
  }

  const labelClass = 'block font-mono text-[10px] text-slate-500 mb-1 tracking-widest uppercase'
  const inputClass = `
    w-full bg-white/5 border border-cyan-500/20 rounded-lg
    font-mono text-sm text-slate-200 px-3 py-2
    focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30
    transition-all duration-150
  `
  const numInputClass = `${inputClass} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`
  const selectClass = `${inputClass} appearance-none cursor-pointer`

  return (
    <div className="space-y-6">
      {/* Operation Type toggle */}
      <div>
        <label className={labelClass}>Operation Type</label>
        <div className="flex gap-2">
          {(['milling', 'turning', 'both'] as const).map(type => (
            <button
              key={type}
              type="button"
              onClick={() => update({ type })}
              className={`
                flex-1 py-2 rounded-lg font-mono text-xs font-semibold tracking-widest uppercase
                border transition-all duration-150
                ${value.type === type
                  ? 'border-cyan-400/60 text-cyan-300 bg-cyan-500/10'
                  : 'border-cyan-500/20 text-slate-500 hover:border-cyan-500/40 hover:text-slate-400'
                }
              `}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Name + Controller */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Machine Name</label>
          <input
            type="text"
            value={value.name}
            onChange={e => { update({ name: e.target.value }); setErrors(p => ({ ...p, name: '' })) }}
            placeholder="e.g. Haas VF-2 Bay 1"
            className={inputClass}
          />
          {errors.name && <p className="font-mono text-[10px] text-red-400 mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className={labelClass}>Controller</label>
          <div className="relative">
            <select
              value={value.controller}
              onChange={e => update({ controller: e.target.value })}
              className={selectClass}
            >
              {CONTROLLERS.map(c => (
                <option key={c} value={c} className="bg-[#0d1526]">{c}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-cyan-500/60 text-xs">▾</div>
          </div>
        </div>
      </div>

      {/* Travel Envelope */}
      <div>
        <label className={labelClass}>Travel Envelope (mm)</label>
        <div className="grid grid-cols-3 gap-3">
          {(['travelX', 'travelY', 'travelZ'] as const).map(axis => (
            <div key={axis} className="flex items-center gap-2">
              <span className="font-mono text-xs text-cyan-500 w-4 shrink-0">{axis.slice(-1)}</span>
              <input
                type="number"
                min={0}
                step={1}
                value={value[axis] || ''}
                onChange={e => update({ [axis]: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                className={numInputClass}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Spindle */}
      <div>
        <label className={labelClass}>Spindle</label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-mono text-[10px] text-slate-600 mb-1">Power (kW)</label>
            <input
              type="number"
              min={0}
              step={0.1}
              value={value.spindlePower || ''}
              onChange={e => update({ spindlePower: parseFloat(e.target.value) || 0 })}
              placeholder="0"
              className={numInputClass}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] text-slate-600 mb-1">Max RPM</label>
            <input
              type="number"
              min={0}
              step={100}
              value={value.maxRpm || ''}
              onChange={e => update({ maxRpm: parseInt(e.target.value) || 0 })}
              placeholder="0"
              className={numInputClass}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] text-slate-600 mb-1">Max Feed (mm/min)</label>
            <input
              type="number"
              min={0}
              step={100}
              value={value.maxFeedRate || ''}
              onChange={e => update({ maxFeedRate: parseInt(e.target.value) || 0 })}
              placeholder="0"
              className={numInputClass}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] text-slate-600 mb-1">Safe Z (mm)</label>
            <input
              type="number"
              min={0}
              step={1}
              value={value.safeZ || ''}
              onChange={e => update({ safeZ: parseFloat(e.target.value) || 0 })}
              placeholder="50"
              className={numInputClass}
            />
          </div>
        </div>
      </div>

      {/* G-Code Header / Footer Templates */}
      <div>
        <button
          type="button"
          onClick={() => setTemplateOpen(o => !o)}
          className="flex items-center gap-2 font-mono text-[10px] text-slate-500 tracking-widest uppercase hover:text-slate-400 transition-colors duration-150"
        >
          <span className={`text-cyan-500/60 text-xs transition-transform duration-150 ${templateOpen ? 'rotate-90' : ''}`}>▸</span>
          G-Code Header / Footer
        </button>
        {templateOpen && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block font-mono text-[10px] text-slate-600 mb-1">Header</label>
              <textarea
                value={value.headerTemplate ?? ''}
                onChange={e => onChange({ ...value, headerTemplate: e.target.value })}
                placeholder={getDefaultForController(value.controller).header}
                rows={4}
                className={`${inputClass} font-mono text-xs resize-y`}
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] text-slate-600 mb-1">Footer</label>
              <textarea
                value={value.footerTemplate ?? ''}
                onChange={e => onChange({ ...value, footerTemplate: e.target.value })}
                placeholder={getDefaultForController(value.controller).footer}
                rows={4}
                className={`${inputClass} font-mono text-xs resize-y`}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const defaults = getDefaultForController(value.controller)
                onChange({ ...value, headerTemplate: defaults.header, footerTemplate: defaults.footer })
              }}
              className="font-mono text-[10px] text-cyan-400/60 hover:text-cyan-400 transition-colors duration-150"
            >
              Reset to defaults
            </button>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-cyan-500/10">
        <button
          type="button"
          onClick={onCancel}
          className="
            font-mono text-xs text-slate-400 border border-slate-700 rounded-lg px-4 py-2
            hover:border-slate-500 hover:text-slate-200 transition-all duration-150
          "
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="
            font-mono text-xs text-white border border-cyan-400/40 rounded-lg px-4 py-2
            bg-cyan-500/10 hover:bg-cyan-500/20 hover:border-cyan-400/60
            transition-all duration-150
          "
        >
          Save Profile
        </button>
      </div>
    </div>
  )
}
